<script setup>
import { ref, computed, onMounted, watch, defineProps } from 'vue'
import { useRoute } from 'vue-router'
import Select from 'primevue/select'
import Chip   from 'primevue/chip'

import { useUserStore } from '@/stores/user'
import { useNotify } from '@/services/useNotify'
import { useI18n } from '@/i18n'
import { useConfirmDelete } from '@/composables/useConfirmDelete'
import { useFormatDate } from '@/composables/useFormatDate'
import AppDialog from '@/components/ui/AppDialog.vue'
import api from '@/api'

const props = defineProps({
  optimized: { type: Boolean, default: false },
})

const emit = defineEmits(['filters-change'])

const user = useUserStore()
const notify = useNotify()
const { activeLang, t, languageOptions } = useI18n()
const { confirmDelete } = useConfirmDelete()
const { fmtDate } = useFormatDate()

const workflows = ref([])
const allTags = ref([])
const total = ref(0)
const loading = ref(false)

const route = useRoute()

// Extra filters (beyond AppTable's built-in search)
const severityFilter    = ref(null)
const featuredFilter    = ref(null)
const visibilityFilter  = ref(null)
const hasStreamsFilter   = ref(null) // null=alle, true=mit Streams, false=ohne
// Set via the stats widgets next to the table (donut slice click)
const tagFilter   = ref(null)
const mitreFilter = ref(null)
const isacaFilter = ref(null)

// Mirror the widget-relevant filter state up to the view so the stats
// panel can mark the active donut slices
watch([severityFilter, visibilityFilter, tagFilter, mitreFilter, isacaFilter], () => {
  emit('filters-change', {
    severity:   severityFilter.value,
    visibility: visibilityFilter.value,
    tag:        tagFilter.value,
    mitre:      mitreFilter.value,
    isaca:      isacaFilter.value,
  })
})

const visibilityLabel = (v) => {
  if (v === 'company')   return t('siemWorkflows.visCompany')
  if (v === 'public')    return t('siemWorkflows.visPublic')
  return t('siemWorkflows.visCommunity')
}

const activeChips = computed(() => {
  const chips = []
  if (severityFilter.value)
    chips.push({ key: 'severity',   label: `${t('siemWorkflows.severity')}: ${severityFilter.value}` })
  if (featuredFilter.value === true)
    chips.push({ key: 'featured',   label: t('siemWorkflows.featured') })
  if (featuredFilter.value === false)
    chips.push({ key: 'featured',   label: t('siemWorkflows.notFeatured') })
  if (visibilityFilter.value)
    chips.push({ key: 'visibility', label: `${t('siemWorkflows.visibility')}: ${visibilityLabel(visibilityFilter.value)}` })
  if (hasStreamsFilter.value === true)
    chips.push({ key: 'has_streams', label: t('siemWorkflows.filterWithStreams') })
  if (hasStreamsFilter.value === false)
    chips.push({ key: 'has_streams', label: t('siemWorkflows.filterWithoutStreams') })
  if (tagFilter.value)
    chips.push({ key: 'tag',   label: `${t('siemWorkflows.chipTag')}: ${tagFilter.value}` })
  if (mitreFilter.value)
    chips.push({ key: 'mitre', label: `${t('siemWorkflows.chipMitre')}: ${mitreFilter.value}` })
  if (isacaFilter.value)
    chips.push({ key: 'isaca', label: `${t('siemWorkflows.chipIsaca')}: ${isacaFilter.value}` })
  return chips
})

const hasAnyFilter = computed(() => activeChips.value.length > 0)

function removeChip(key) {
  if (key === 'severity')    severityFilter.value   = null
  if (key === 'featured')    featuredFilter.value   = null
  if (key === 'visibility')  visibilityFilter.value = null
  if (key === 'has_streams') hasStreamsFilter.value  = null
  if (key === 'tag')         tagFilter.value        = null
  if (key === 'mitre')       mitreFilter.value      = null
  if (key === 'isaca')       isacaFilter.value      = null
  onFilterChange()
}

// Applied from the stats widgets (donut slice click) — toggle semantics:
// clicking the active value again clears that filter.
function applyExternalFilter({ dimension, value }) {
  const targets = {
    severity:   severityFilter,
    visibility: visibilityFilter,
    tag:        tagFilter,
    mitre:      mitreFilter,
    isaca:      isacaFilter,
  }
  const target = targets[dimension]
  if (!target) return
  target.value = target.value === value ? null : value
  onFilterChange()
}

defineExpose({ applyExternalFilter })

/* Dialog / Edit */
const showDialog = ref(false)
const isAdding = ref(false)
const editingWorkflow = ref({ id: null, tags: [], severity: 'INFO', featured: false, visibility: 'community' })
const workflowLang = ref(activeLang.value)
const workflowTranslations = ref({})
const workflowTranslationOriginals = ref({})
const baseWorkflowSnapshot = ref({ name: '', description: '' })

const columns = [
  { field: 'featured',    header: 'Featured',                              sortable: true,  default: true  },
  { field: 'visibility',  header: t('siemWorkflows.visibility'),           sortable: true,  default: true  },
  { field: 'has_mitre',   header: 'MITRE',                                 sortable: true,  default: true  },
  { field: 'has_isaca',   header: 'ISACA',                                 sortable: false, default: false },
  { field: 'name',        header: 'Name',                                  sortable: true,  default: true,  minWidth: '220px' },
  { field: 'description', header: t('siemWorkflows.description'),          sortable: false, default: true,  minWidth: '280px' },
  { field: 'severity',    header: 'Severity',                              sortable: true,  default: true  },
  { field: 'company_name',header: t('siemWorkflows.colCompany'),           sortable: false, default: false },
  { field: 'tag_names',    header: 'Tags',                                  sortable: false, default: true  },
  { field: 'stream_count', header: t('siemWorkflows.colStreams'),           sortable: true,  default: true  },
]

const severities = ['INFO', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL']

const severityTag = (s) => {
  switch (s) {
    case 'INFO':     return 'secondary'
    case 'LOW':      return 'info'
    case 'MEDIUM':   return 'warning'
    case 'HIGH':     return 'danger'
    case 'CRITICAL': return 'contrast'
    default:         return null
  }
}

const isBrowser = typeof document !== 'undefined'
const stripHtmlTags = (html = '') => {
  if (!html) return ''
  if (isBrowser) {
    const el = document.createElement('div')
    el.innerHTML = html
    return (el.textContent || el.innerText || '').replace(/\s+/g, ' ').trim()
  }
  return html.replace(/<\/?[^>]+(>|$)/g, '').replace(/\s+/g, ' ').trim()
}

const truncateDescription = (description, maxLength = 220) => {
  const cleaned = stripHtmlTags(description)
  if (!cleaned) return ''
  return cleaned.length > maxLength ? cleaned.substring(0, maxLength) + '…' : cleaned
}

const toNumberArray = (arr) => (arr || []).map(v => Number(v)).filter(v => !Number.isNaN(v))

const englishFallbackFromRow = (row) => ({ name: row?.english_name || row?.name || '', description: row?.english_description || row?.description || '' })

const resetTranslationDrafts = () => {
  const drafts = {}
  const originals = {}
  languageOptions.forEach(opt => {
    drafts[opt.code] = { name: '', description: '' }
    originals[opt.code] = { name: '', description: '' }
  })
  workflowTranslations.value = drafts
  workflowTranslationOriginals.value = originals
}

const ensureTranslationDraft = (lang) => {
  if (!lang) return { name: '', description: '' }
  if (!workflowTranslations.value[lang]) workflowTranslations.value[lang] = { name: '', description: '' }
  if (!workflowTranslationOriginals.value[lang]) workflowTranslationOriginals.value[lang] = { name: '', description: '' }
  return workflowTranslations.value[lang]
}

const translationDraft = (lang) => ensureTranslationDraft(lang)

const setTranslationDraft = (lang, values = {}, { setOriginal = false, onlyIfEmpty = false } = {}) => {
  if (!lang) return
  const draft = ensureTranslationDraft(lang)
  const nextName = values.name ?? ''
  const nextDesc = values.description ?? ''
  if (!onlyIfEmpty || !draft.name) draft.name = nextName
  if (!onlyIfEmpty || !draft.description) draft.description = nextDesc
  if (setOriginal) workflowTranslationOriginals.value[lang] = { name: draft.name, description: draft.description }
}

const translationHasContent = (draft) => {
  if (!draft) return false
  return Boolean((draft.name || '').trim() || stripHtmlTags(draft.description || '').trim())
}

const translationChanged = (lang, draft) => {
  const original = workflowTranslationOriginals.value[lang] || { name: '', description: '' }
  return (draft.name || '') !== (original.name || '') || (draft.description || '') !== (original.description || '')
}

watch(activeLang, (lang) => {
  if (!showDialog.value) workflowLang.value = lang
  fetchWorkflows()
})

async function fetchTags() {
  try {
    const res = await api.getTags()
    const tagList = Array.isArray(res.data) ? res.data : (res.data?.tags ?? [])
    allTags.value = tagList.map(t => ({ id: Number(t.id), name: t.name }))
  } catch (e) {
    notify.error(t('siemWorkflowsNotifications.tagsLoadFailed'), e?.response?.data?.message || e.message)
  }
}

async function fetchWorkflows(q = {}) {
  loading.value = true
  try {
    if (route.query.featured === 'true' && featuredFilter.value === null) featuredFilter.value = true
    if (route.query.severity && !severityFilter.value) severityFilter.value = route.query.severity

    const params = {
      page:       q.page  ?? 1,
      limit:      q.limit ?? 25,
      lang:       activeLang.value,
      q:          q.search || undefined,
      sortBy:     q.sortField || undefined,
      sortDir:    q.sortOrder || undefined,
      severity:    severityFilter.value || undefined,
      featured:    featuredFilter.value ?? undefined,
      visibility:  visibilityFilter.value || undefined,
      has_streams: hasStreamsFilter.value ?? undefined,
      tag:             tagFilter.value   || undefined,
      mitre_tactic:    mitreFilter.value || undefined,
      isaca_use_case:  isacaFilter.value || undefined,
    }
    const fetcher = props.optimized ? api.getWorkflowsOptimized : api.getWorkflows
    const res = await fetcher(params)
    workflows.value = res.data?.workflows || []
    total.value = res.data?.total ?? workflows.value.length
  } catch (e) {
    notify.error(t('siemWorkflowsNotifications.workflowsLoadFailed'), e?.response?.data?.message || e.message)
  } finally {
    loading.value = false
  }
}

function onQueryChange(q) { fetchWorkflows(q) }

function onFilterChange() { fetchWorkflows({ page: 1 }) }

function resetFilters() {
  severityFilter.value   = null
  featuredFilter.value   = null
  visibilityFilter.value = null
  hasStreamsFilter.value  = null
  tagFilter.value        = null
  mitreFilter.value      = null
  isacaFilter.value      = null
  fetchWorkflows({ page: 1 })
}

/* Dialog */
function openCreate() {
  isAdding.value = true
  workflowLang.value = activeLang.value
  editingWorkflow.value = { id: null, tags: [], severity: 'INFO', featured: false, visibility: 'community' }
  baseWorkflowSnapshot.value = { name: '', description: '' }
  resetTranslationDrafts()
  showDialog.value = true
}

async function openEdit(row) {
  isAdding.value = false
  workflowLang.value = activeLang.value
  editingWorkflow.value = {
    id: row.id,
    tags: toNumberArray(row.tags || row.tag_ids),
    severity: row.severity || 'INFO',
    featured: !!row.featured,
    visibility: row.visibility || 'community',
  }
  baseWorkflowSnapshot.value = { name: '', description: '' }
  resetTranslationDrafts()
  setTranslationDraft('en', englishFallbackFromRow(row), { setOriginal: true, onlyIfEmpty: true })
  showDialog.value = true
  await loadWorkflowTranslations(row)
}

async function loadWorkflowTranslations(row) {
  if (!row?.id) return
  try {
    const [translationsRes, englishRes] = await Promise.all([
      api.getWorkflowTranslations(row.id),
      api.getWorkflow(row.id, { lang: 'en' }),
    ])
    const translations = Array.isArray(translationsRes.data) ? translationsRes.data : []
    translations.forEach(item => setTranslationDraft(item.language_code, item, { setOriginal: true }))
    if (englishRes?.data) {
      baseWorkflowSnapshot.value = { name: englishRes.data.name || '', description: englishRes.data.description || '' }
      const hasEnglishOriginal = Boolean(
        (workflowTranslationOriginals.value.en?.name || '').trim() ||
        (workflowTranslationOriginals.value.en?.description || '').trim()
      )
      setTranslationDraft('en', baseWorkflowSnapshot.value, { setOriginal: !hasEnglishOriginal, onlyIfEmpty: true })
    }
    if (row?.name || row?.description) {
      setTranslationDraft(activeLang.value, { name: row.name || '', description: row.description || '' }, { onlyIfEmpty: true })
    }
    if (!baseWorkflowSnapshot.value.name && translationDraft('en')?.name) {
      baseWorkflowSnapshot.value = { ...translationDraft('en') }
    }
  } catch (e) {
    notify.error(t('siemWorkflowsNotifications.workflowsLoadFailed'), e?.response?.data?.message || e.message)
  }
}

function closeDialog() { showDialog.value = false }

async function saveWorkflow() {
  const currentDraft = translationDraft(workflowLang.value)
  const englishDraft = translationDraft('en')
  const baseName = (englishDraft.name || baseWorkflowSnapshot.value.name || currentDraft.name || '').trim()
  const baseDescription = englishDraft.description || baseWorkflowSnapshot.value.description || currentDraft.description || ''

  const data = {
    name: baseName,
    description: baseDescription,
    tags: toNumberArray(editingWorkflow.value.tags),
    severity: editingWorkflow.value.severity,
    featured: !!editingWorkflow.value.featured,
    visibility: editingWorkflow.value.visibility || 'community',
    language_code: 'en',
  }

  try {
    let workflowId = editingWorkflow.value.id
    if (isAdding.value) {
      const res = await api.createWorkflow(data)
      workflowId = res.data?.id || workflowId
      notify.success(t('siemWorkflowsNotifications.workflowSaved'), t('siemWorkflowsNotifications.workflowCreated'))
    } else {
      await api.updateWorkflow(editingWorkflow.value.id, data)
      notify.success(t('siemWorkflowsNotifications.workflowSaved'), t('siemWorkflowsNotifications.workflowUpdated'))
    }
    if (workflowId) await saveTranslationUpdates(workflowId)
    closeDialog()
    fetchWorkflows()
  } catch (e) {
    notify.error(t('siemWorkflowsNotifications.saveFailed'), e?.response?.data?.message || e.message)
  }
}

const saveTranslationUpdates = async (workflowId) => {
  const updates = []
  Object.entries(workflowTranslations.value).forEach(([lang, draft]) => {
    if (!draft || !translationHasContent(draft)) return
    const shouldUpdate = isAdding.value || translationChanged(lang, draft)
    if (!shouldUpdate) return
    updates.push(api.updateWorkflowTranslation(workflowId, lang, { name: draft.name, description: draft.description }))
  })
  if (updates.length) await Promise.all(updates)
}

async function removeWorkflows(rows) {
  if (!await confirmDelete(t('siemWorkflowsNotifications.confirmDelete'))) return
  try {
    await Promise.all(rows.map(r => api.deleteWorkflow(r.id)))
    notify.success(t('siemWorkflowsNotifications.workflowDeleted'), t('siemWorkflowsNotifications.workflowRemoved'))
    fetchWorkflows()
  } catch (e) {
    notify.error(t('siemWorkflowsNotifications.deleteFailed'), e?.response?.data?.message || e.message)
  }
}

const tableActions = computed(() => [
  user.can('siem_workflow.change') && {
    label:          t('siemWorkflows.editTooltip'),
    icon:           'pi pi-file-edit',
    requiresSingle: true,
    command:        (rows) => openEdit(rows[0]),
  },
  user.can('siem_workflow.delete') && {
    label:     t('siemWorkflows.deleteTooltip'),
    icon:      'pi pi-trash',
    severity:  'danger',
    separator: true,
    command:   removeWorkflows,
  },
].filter(Boolean))

onMounted(async () => { await Promise.all([fetchTags(), fetchWorkflows()]) })
</script>

<template>
  <div class="workflows-overview">

    <!-- ── Filter-Karte ─────────────────────────────────────────────────────── -->
    <div class="hw-card filter-card">
      <div class="hw-card-header">
        <i class="pi pi-filter hw-card-icon" />
        <span class="hw-card-title">{{ t('siemWorkflows.filtersTitle') }}</span>
        <pButton
          v-if="hasAnyFilter"
          icon="pi pi-times"
          :label="t('siemWorkflows.filterReset')"
          size="small"
          severity="secondary"
          text
          @click="resetFilters"
        />
      </div>

      <div class="filter-grid">
        <div class="filter-field">
          <label>{{ t('siemWorkflows.severity') }}</label>
          <Select
            v-model="severityFilter"
            :options="severities"
            :placeholder="t('siemWorkflows.filterSeverityPlaceholder')"
            show-clear
            class="w-full"
            @change="onFilterChange"
          />
        </div>
        <div class="filter-field">
          <label>Featured</label>
          <Select
            v-model="featuredFilter"
            :options="[{ label: t('siemWorkflows.featured'), value: true }, { label: t('siemWorkflows.notFeatured'), value: false }]"
            option-label="label"
            option-value="value"
            :placeholder="t('siemWorkflows.filterFeaturedPlaceholder')"
            show-clear
            class="w-full"
            @change="onFilterChange"
          />
        </div>
        <div class="filter-field">
          <label>{{ t('siemWorkflows.filterStreamsLabel') }}</label>
          <Select
            v-model="hasStreamsFilter"
            :options="[
              { label: t('siemWorkflows.filterWithStreams'),    value: true  },
              { label: t('siemWorkflows.filterWithoutStreams'), value: false },
            ]"
            option-label="label"
            option-value="value"
            :placeholder="t('siemWorkflows.filterStreamsPlaceholder')"
            show-clear
            class="w-full"
            @change="onFilterChange"
          />
        </div>
        <div class="filter-field">
          <label>{{ t('siemWorkflows.visibility') }}</label>
          <Select
            v-model="visibilityFilter"
            :options="[
              { label: t('siemWorkflows.visCompany'),   value: 'company'   },
              { label: t('siemWorkflows.visCommunity'), value: 'community' },
              { label: t('siemWorkflows.visPublic'),    value: 'public'    },
            ]"
            option-label="label"
            option-value="value"
            :placeholder="t('siemWorkflows.filterVisibilityPlaceholder')"
            show-clear
            class="w-full"
            @change="onFilterChange"
          />
        </div>
      </div>

      <div v-if="hasAnyFilter" class="filter-chips">
        <Chip
          v-for="chip in activeChips"
          :key="chip.key"
          :label="chip.label"
          removable
          @remove="removeChip(chip.key)"
        />
      </div>
    </div>

    <!-- ── Tabelle ───────────────────────────────────────────────────────────── -->
    <AppTable
      table-key="siem-workflows"
      :columns="columns"
      :data="workflows"
      :total="total"
      :loading="loading"
      lazy
      :actions="tableActions"
      @refresh="fetchWorkflows()"
      @query-change="onQueryChange"
    >
      <template #toolbar-left>
        <pButton
          v-if="user.can('siem_workflow.create')"
          :label="t('siemWorkflows.add')"
          icon="pi pi-plus"
          size="small"
          @click="openCreate"
        />
      </template>

      <!-- Featured -->
      <template #cell-featured="{ row }">
        <i v-if="row.featured" class="pi pi-star-fill" style="color: var(--p-warn)" />
        <i v-else class="pi pi-star" style="color: var(--p-text-color-secondary)" />
      </template>

      <!-- Visibility -->
      <template #cell-visibility="{ row }">
        <Tag
          v-if="row.visibility === 'company'"
          :value="t('siemWorkflows.visCompany')"
          severity="secondary"
          v-tooltip.top="t('siemWorkflows.visCompanyHint')"
        />
        <Tag
          v-else-if="row.visibility === 'public'"
          :value="t('siemWorkflows.visPublic')"
          severity="success"
          v-tooltip.top="t('siemWorkflows.visPublicHint')"
        />
        <Tag
          v-else
          :value="t('siemWorkflows.visCommunity')"
          severity="info"
          v-tooltip.top="t('siemWorkflows.visCommunityHint')"
        />
      </template>

      <!-- Company attribution -->
      <template #cell-company_name="{ row }">
        <span v-if="row.company_name" class="company-badge">
          <i class="pi pi-building" style="font-size:.7rem;opacity:.6" />
          {{ row.company_name }}
        </span>
        <span v-else class="text-secondary" style="opacity:.4">—</span>
      </template>

      <!-- MITRE -->
      <template #cell-has_mitre="{ row }">
        <Tag v-if="row.has_mitre" :value="t('siemWorkflows.mapped')" severity="info" />
        <Tag v-else :value="t('siemWorkflows.none')" severity="secondary" />
      </template>

      <!-- ISACA -->
      <template #cell-has_isaca="{ row }">
        <Tag v-if="row.has_isaca" :value="t('siemWorkflows.mapped')" severity="info" />
        <Tag v-else :value="t('siemWorkflows.none')" severity="secondary" />
      </template>

      <!-- Name: link + dates -->
      <template #cell-name="{ row }">
        <router-link :to="'/detection/workflows/' + row.id" class="hw-link">
          <strong>{{ row.name }}</strong>
        </router-link>
        <div class="meta-dates">
          <span>{{ t('siemWorkflows.createdAt') }}: {{ fmtDate(row.created_at) }}</span>
          <span>{{ t('siemWorkflows.updatedAt') }}: {{ fmtDate(row.updated_at) }}</span>
        </div>
      </template>

      <!-- Description (truncated) -->
      <template #cell-description="{ row }">
        {{ truncateDescription(row.description, 220) }}
      </template>

      <!-- Severity -->
      <template #cell-severity="{ row }">
        <Tag :value="row.severity" :severity="severityTag(row.severity)" />
      </template>

      <!-- Tags -->
      <template #cell-tag_names="{ row }">
        <div class="tag-list">
          <Tag
            v-for="(tagName, i) in row.tag_names"
            :key="`${row.id}-${i}`"
            severity="secondary"
            :value="tagName"
          />
        </div>
      </template>

      <template #cell-stream_count="{ row }">
        <span
          v-if="row.stream_count > 0"
          class="stream-count-badge stream-count-badge--ok"
          v-tooltip.top="`${row.stream_count} ${row.stream_count === 1 ? 'Stream' : 'Streams'} ${t('siemWorkflows.streamsLinkedSuffix')}`"
        >
          <i class="pi pi-check-circle" />
          {{ row.stream_count }}
        </span>
        <span
          v-else
          class="stream-count-badge stream-count-badge--none"
          v-tooltip.top="t('siemWorkflows.streamsNone')"
        >
          <i class="pi pi-minus-circle" />
          {{ t('siemWorkflows.streamsNone') }}
        </span>
      </template>

    </AppTable>

    <!-- ── Dialog ───────────────────────────────────────────────────────────── -->
    <AppDialog
      v-model="showDialog"
      :title="isAdding ? t('siemWorkflows.dialogCreate') : t('siemWorkflows.dialogEdit')"
      size="xl"
      :showFooter="true"
      :maximizable="true"
      @cancel="closeDialog"
      @submit="saveWorkflow"
    >
      <div class="flex flex-column gap-4">
        <div class="grid formgrid">
          <div class="col-12 md:col-8">
            <label for="name" class="block mb-2">{{ t('siemWorkflows.formName') }}</label>
            <InputText id="name" v-model="translationDraft(workflowLang).name" fluid />
          </div>
          <div class="col-12 md:col-2">
            <label for="workflow-language" class="block mb-2">{{ t('siemWorkflows.formLanguage') }}</label>
            <Select id="workflow-language" v-model="workflowLang" :options="languageOptions" optionLabel="label" optionValue="code" />
          </div>
          <div class="col-12 md:col-2 flex align-items-end pb-2">
            <div class="flex align-items-center gap-2">
              <Checkbox id="featured" v-model="editingWorkflow.featured" :binary="true" />
              <label for="featured">{{ t('siemWorkflows.formFeatured') }}</label>
            </div>
          </div>
          <div class="col-12 md:col-4">
            <label for="visibility" class="block mb-2">{{ t('siemWorkflows.visibility') }}</label>
            <Select
              id="visibility"
              v-model="editingWorkflow.visibility"
              :options="[
                { label: t('siemWorkflows.visCompany'),   value: 'company',   description: t('siemWorkflows.visCompanyHint')   },
                { label: t('siemWorkflows.visCommunity'), value: 'community', description: t('siemWorkflows.visCommunityHint') },
                { label: t('siemWorkflows.visPublic'),    value: 'public',    description: t('siemWorkflows.visPublicHint')    },
              ]"
              optionLabel="label"
              optionValue="value"
              class="w-full"
            />
          </div>
          <div class="col-12 md:col-2">
            <label for="severity" class="block mb-2">{{ t('siemWorkflows.formSeverity') }}</label>
            <Select id="severity" v-model="editingWorkflow.severity" :options="severities" class="w-full" />
          </div>
          <div class="col-12 md:col-6">
            <label for="tags" class="block mb-2">{{ t('siemWorkflows.formTags') }}</label>
            <MultiSelect
              id="tags"
              v-model="editingWorkflow.tags"
              :options="allTags"
              optionLabel="name"
              optionValue="id"
              filter
              :placeholder="t('siemWorkflows.filterTagsPlaceholder')"
              :maxSelectedLabels="3"
              class="w-full"
              display="chip"
            />
          </div>
          <div class="col-12">
            <label for="description" class="block mb-2">{{ t('siemWorkflows.formDescription') }}</label>
            <Editor id="description" v-model="translationDraft(workflowLang).description" editor-style="min-height: 305px;" />
          </div>
        </div>
      </div>
    </AppDialog>
  </div>
</template>

<style scoped>
.workflows-overview {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ── Filter-Karte ───────────────────────────────────────────────────────────── */
.filter-card { padding-bottom: 12px; }

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px 14px;
  padding: 8px 14px 4px;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.filter-field label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--p-text-color-secondary);
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 14px 0;
}
.filter-chips :deep(.p-chip) {
  font-size: 0.78rem;
  padding: 2px 8px;
}

/* ── Tabellen-Zellen ────────────────────────────────────────────────────────── */
.hw-link { color: var(--p-text-color); text-decoration: none; }
.hw-link:hover { color: var(--p-primary-color); text-decoration: underline; }

.company-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: .8rem;
  font-weight: 600;
  color: var(--p-text-color-secondary);
}
.meta-dates {
  display: flex;
  flex-direction: column;
  font-size: 0.72rem;
  color: var(--p-text-muted-color);
  margin-top: 2px;
  line-height: 1.4;
}
.tag-list { display: flex; flex-wrap: wrap; gap: 2px 4px; }

.stream-count-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}
.stream-count-badge--ok   { color: var(--p-green-600, #16a34a); }
.stream-count-badge--none { color: var(--p-text-color-secondary); opacity: .6; font-weight: 400; }
:global(.my-app-dark) .stream-count-badge--ok { color: var(--p-green-400, #4ade80); }
</style>
